import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { BACKEND_URL } from '../config';
import { toast } from '../components/Toast';
import { confirm } from '../components/ConfirmDialog';
import DjamannLoader from '../components/DjamannLoader';
import { Building2, FileText, Settings, AlertTriangle, Lightbulb, BarChart2, Lock, Trash2, Save, Loader2 } from 'lucide-react';
import logger from '../utils/logger';

const BusinessSettings = () => {
  const [settings, setSettings] = useState({
    nom_entreprise: '',
    adresse: '',
    telephone: '',
    email: '',
    site_web: '',
    numero_registre: '',
    numero_fiscal: '',
    logo: '',
    devise: 'XOF',
    conditions_paiement: '',
    mentions_legales: '',
    prefixe_facture: 'FAC-',
    prefixe_devis: 'DEV-',
    prefixe_recu: 'REC-'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [uploading, setUploading] = useState(false);
  const [logoInputMode, setLogoInputMode] = useState('url'); // 'url' ou 'upload'
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await api.get('/business/settings');
      if (res.data) {
        setSettings(s => ({ ...s, ...res.data }));
      }
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fonction d'upload du logo
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Vérifier le type de fichier
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.warning('Format non supporté. Utilisez JPG, PNG, GIF, WebP ou SVG.');
      return;
    }

    // Vérifier la taille (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.warning('Le fichier est trop volumineux. Taille max: 2MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await api.post('/business/upload-logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.url) {
        setSettings(s => ({ ...s, logo: response.data.url }));
        toast.success('Logo uploadé avec succès !');
      }
    } catch (err) {
      logger.error('Erreur upload:', err);
      toast.error(err.response?.data?.error || 'Erreur lors de l\'upload du logo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Supprimer le logo
  const handleRemoveLogo = () => {
    setSettings(s => ({ ...s, logo: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/business/settings', settings);
      toast.success('Paramètres enregistrés avec succès !');
    } catch (err) {
      logger.error(err);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleResetWorkspace = async () => {
    // Double confirmation pour éviter les erreurs
    const confirm1 = await confirm(
      'ATTENTION !\n\nCette action va supprimer TOUTES vos données :\n' +
      '• Factures\n• Devis\n• Paiements\n• Dépenses\n• Prestations\n• Dettes\n• Clients\n• Produits\n\n' +
      'Cette action est IRRÉVERSIBLE !\n\nÊtes-vous sûr de vouloir continuer ?'
    );
    
    if (!confirm1) return;
    
    const confirmText = window.prompt(
      'Pour confirmer la réinitialisation, tapez "REINITIALISER" en majuscules :'
    );
    
    if (confirmText !== 'REINITIALISER') {
      toast.info('Réinitialisation annulée.');
      return;
    }
    
    setResetting(true);
    try {
      await api.delete('/business/reset');
      toast.success('Toutes les données ont été réinitialisées avec succès !');
      // Recharger les paramètres (maintenant vides)
      loadSettings();
    } catch (err) {
      logger.error(err);
      toast.error('Erreur lors de la réinitialisation');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return <DjamannLoader variant="section" />;
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-800">Paramètres</h1>
        <p className="text-gray-500 text-sm">Configurez votre espace de travail</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow mb-6">
        <div className="flex border-b">
          {[
            { id: 'general', label: 'Général', Icon: Building2 },
            { id: 'facturation', label: 'Facturation', Icon: FileText },
            { id: 'preferences', label: 'Préférences', Icon: Settings }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-4 text-sm font-medium transition border-b-2 flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'border-indigo-600 text-indigo-600 bg-indigo-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.Icon size={16} /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* General Tab */}
        {activeTab === 'general' && (
          <div className="bg-white rounded-xl shadow p-6 space-y-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Informations de l'entreprise</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l'entreprise</label>
                <input
                  type="text"
                  value={settings.nom_entreprise || ''}
                  onChange={(e) => setSettings({ ...settings, nom_entreprise: e.target.value })}
                  className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                  placeholder="Votre entreprise"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                <textarea
                  value={settings.adresse || ''}
                  onChange={(e) => setSettings({ ...settings, adresse: e.target.value })}
                  rows="2"
                  className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                  placeholder="Adresse complète"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input
                  type="text"
                  value={settings.telephone || ''}
                  onChange={(e) => setSettings({ ...settings, telephone: e.target.value })}
                  className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                  placeholder="+225 XX XX XX XX"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={settings.email || ''}
                  onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                  className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                  placeholder="contact@entreprise.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Site web</label>
                <input
                  type="url"
                  value={settings.site_web || ''}
                  onChange={(e) => setSettings({ ...settings, site_web: e.target.value })}
                  className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                  placeholder="https://www.exemple.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">N° Fiscal / RCCM</label>
                <input
                  type="text"
                  value={settings.numero_fiscal || ''}
                  onChange={(e) => setSettings({ ...settings, numero_fiscal: e.target.value })}
                  className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                  placeholder="Numéro d'identification"
                />
              </div>
            </div>

            {/* Logo Section - Améliorée */}
            <div className="border-t pt-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">Logo de l'entreprise</label>
              
              {/* Aperçu actuel du logo */}
              {settings.logo && (
                <div className="mb-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <img 
                        src={settings.logo.startsWith('http') ? settings.logo : `${BACKEND_URL}${settings.logo}`}
                        alt="Logo actuel" 
                        className="h-16 max-w-32 object-contain bg-white rounded-lg p-2 shadow-sm"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>';
                        }}
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Logo actuel</p>
                        <p className="text-xs text-gray-400 truncate max-w-xs">{settings.logo}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveLogo}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
                      title="Supprimer le logo"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              {/* Toggle URL / Upload */}
              <div className="flex gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => setLogoInputMode('upload')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 transition font-medium ${
                    logoInputMode === 'upload'
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Uploader un fichier
                </button>
                <button
                  type="button"
                  onClick={() => setLogoInputMode('url')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 transition font-medium ${
                    logoInputMode === 'url'
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  URL externe
                </button>
              </div>

              {/* Mode Upload */}
              {logoInputMode === 'upload' && (
                <div className="space-y-3">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleLogoUpload}
                    accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                    className="hidden"
                    id="logo-upload"
                  />
                  <label
                    htmlFor="logo-upload"
                    className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer transition ${
                      uploading 
                        ? 'border-indigo-300 bg-indigo-50' 
                        : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
                    }`}
                  >
                    {uploading ? (
                      <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-500 border-t-transparent mb-2"></div>
                        <span className="text-sm text-indigo-600">Upload en cours...</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <svg className="w-10 h-10 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <span className="text-sm font-medium text-gray-600">Cliquez pour sélectionner une image</span>
                        <span className="text-xs text-gray-400 mt-1">JPG, PNG, GIF, WebP ou SVG (max 2MB)</span>
                      </div>
                    )}
                  </label>
                </div>
              )}

              {/* Mode URL */}
              {logoInputMode === 'url' && (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={settings.logo || ''}
                      onChange={(e) => setSettings({ ...settings, logo: e.target.value })}
                      className="flex-1 border rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500"
                      placeholder="https://exemple.com/mon-logo.png"
                    />
                    {settings.logo && (
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        className="px-4 py-2 text-red-500 hover:bg-red-50 rounded-xl border border-red-200 transition"
                      >
                        Effacer
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 flex items-center gap-1">
                    <Lightbulb size={14} className="inline flex-shrink-0" /> Collez l'URL d'une image hébergée en ligne (Google Drive, Imgur, etc.)
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Facturation Tab */}
        {activeTab === 'facturation' && (
          <div className="bg-white rounded-xl shadow p-6 space-y-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Paramètres de facturation</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Préfixe factures</label>
                <input
                  type="text"
                  value={settings.prefixe_facture || ''}
                  onChange={(e) => setSettings({ ...settings, prefixe_facture: e.target.value })}
                  className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                  placeholder="FAC-"
                />
                <p className="text-xs text-gray-400 mt-1">Ex: FAC-2024-001</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Préfixe devis</label>
                <input
                  type="text"
                  value={settings.prefixe_devis || ''}
                  onChange={(e) => setSettings({ ...settings, prefixe_devis: e.target.value })}
                  className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                  placeholder="DEV-"
                />
                <p className="text-xs text-gray-400 mt-1">Ex: DEV-2024-001</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Devise par défaut</label>
                <select
                  value={settings.devise}
                  onChange={(e) => setSettings({ ...settings, devise: e.target.value })}
                  className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="XOF">XOF (Franc CFA)</option>
                  <option value="EUR">EUR (Euro)</option>
                  <option value="USD">USD (Dollar US)</option>
                  <option value="XAF">XAF (Franc CFA CEMAC)</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Conditions de paiement par défaut</label>
              <textarea
                value={settings.conditions_paiement || ''}
                onChange={(e) => setSettings({ ...settings, conditions_paiement: e.target.value })}
                rows="3"
                className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                placeholder="Ex: Paiement à 30 jours. En cas de retard..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mentions légales (bas de facture)</label>
              <textarea
                value={settings.mentions_legales || ''}
                onChange={(e) => setSettings({ ...settings, mentions_legales: e.target.value })}
                rows="2"
                className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                placeholder="Ex: Merci pour votre confiance !"
              />
            </div>
          </div>
        )}

        {/* Preferences Tab */}
        {activeTab === 'preferences' && (
          <div className="bg-white rounded-xl shadow p-6 space-y-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Préférences</h2>
            
            <div className="space-y-4">
              {/* Info box */}
              <div className="p-4 bg-indigo-50 rounded-xl">
                <h3 className="font-medium text-indigo-800 mb-2 flex items-center gap-2"><Lightbulb size={18} /> Conseils</h3>
                <ul className="text-sm text-indigo-600 space-y-1">
                  <li>• Complétez les informations de votre entreprise pour des factures professionnelles</li>
                  <li>• Ajoutez un logo pour personnaliser vos documents</li>
                  <li>• Définissez des conditions de paiement claires</li>
                </ul>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="text-2xl mb-1"><BarChart2 size={28} className="text-gray-600" /></div>
                  <div className="text-sm text-gray-600">Données stockées</div>
                  <p className="text-xs text-gray-400 mt-1">
                    Vos données sont sauvegardées et sécurisées
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="text-2xl mb-1"><Lock size={28} className="text-gray-600" /></div>
                  <div className="text-sm text-gray-600">Accès privé</div>
                  <p className="text-xs text-gray-400 mt-1">
                    Seul vous avez accès à ces données
                  </p>
                </div>
              </div>

              {/* Danger Zone */}
              <div className="border-t pt-6 mt-6">
                <h3 className="font-medium text-red-600 mb-3 flex items-center gap-2"><AlertTriangle size={18} /> Zone de danger</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Cette action supprimera définitivement toutes vos données : factures, devis, paiements, dépenses, prestations, dettes, clients et produits.
                </p>
                <button
                  type="button"
                  onClick={handleResetWorkspace}
                  disabled={resetting}
                  className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition disabled:opacity-50 flex items-center gap-2"
                >
                  {resetting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Réinitialisation en cours...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} /> Réinitialiser toutes les données
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="mt-6 flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-8 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition disabled:opacity-50 font-medium"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <Loader2 size={16} className="animate-spin" /> Enregistrement...
              </span>
            ) : (
              <span className="flex items-center gap-2"><Save size={16} /> Enregistrer les modifications</span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BusinessSettings;
